import axios from 'axios'
import { supabase } from '@/lib/supabase'
import { getCsrfToken, requiresCsrfToken } from '@/lib/csrf'

// Inline function to avoid circular dependency with ./auth
// This was previously imported from ./auth but caused 'cannot access before initialization' errors
const clearSupabaseSession = async () => {
  try {
    await supabase.auth.signOut()
  } catch (error) {
    console.error('Failed to clear Supabase session', error)
  }
}

const resolveApiBaseUrl = (
  locationOverride?: { hostname?: string; protocol?: string },
): string => {
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl && envUrl.trim().length > 0) {
    return envUrl
  }

  const currentLocation =
    locationOverride ||
    (typeof window !== 'undefined'
      ? { hostname: window.location.hostname, protocol: window.location.protocol }
      : undefined)

  if (currentLocation) {
    const { hostname = '', protocol = '' } = currentLocation
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'

    if (isLocalhost) {
      // Local development fallback
      return 'http://localhost:8000'
    }

    // Production/previews fallback
    if (hostname.endsWith('trdrhub.com') || hostname.endsWith('.vercel.app')) {
      return 'https://api.trdrhub.com'
    }

    // Generic HTTPS fallback to avoid mixed-content issues
    if (protocol === 'https:') {
      return 'https://api.trdrhub.com'
    }
  }

  // Default fallback (node/server contexts)
  return 'https://api.trdrhub.com'
}

const buildLoginRedirectUrl = (
  locationOverride?: { pathname?: string; search?: string; hash?: string },
): string => {
  const pathname = locationOverride?.pathname || ''
  const search = locationOverride?.search || ''
  const hash = locationOverride?.hash || ''
  const returnUrl = `${pathname}${search}${hash}` || '/'

  return `/login?returnUrl=${encodeURIComponent(returnUrl)}`
}

const API_BASE_URL_VALUE = resolveApiBaseUrl()
const GUEST_MODE = (import.meta.env.VITE_GUEST_MODE || '').toString().toLowerCase() === 'true'
const AUTH_FREE_PATHS = ['/auth/login', '/auth/register']

const DEFAULT_TIMEOUT_MS = 30000
const LONG_REQUEST_TIMEOUT_MS = 300000 // 5 minutes for large uploads
const SESSION_FETCH_TIMEOUT_MS = 3000
const LONG_REQUEST_PATHS = [
  '/api/validate', 
  '/api/legacy_validate', 
  '/api/legacy-validate',
  '/admin/rulesets/upload', // Large ruleset uploads (ISBP 821 has 300+ rules)
  '/admin/rulesets',        // Ruleset operations
]

const api = axios.create({
  baseURL: API_BASE_URL_VALUE,
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: true, // Include cookies for CSRF token
})

type StoredSupabaseSession = {
  access_token?: string
  expires_at?: number
}

const getStoredSupabaseAccessToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const candidateKeys = new Set<string>()
  const projectRef = import.meta.env.VITE_SUPABASE_PROJECT_REF
  if (projectRef) {
    candidateKeys.add(`sb-${projectRef}-auth-token`)
  }

  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
      candidateKeys.add(key)
    }
  }

  for (const key of candidateKeys) {
    const raw = localStorage.getItem(key)
    if (!raw) continue

    try {
      const parsed = JSON.parse(raw) as StoredSupabaseSession
      const accessToken = parsed?.access_token
      const expiresAt = Number(parsed?.expires_at ?? 0)
      const hasFreshExpiry =
        Number.isFinite(expiresAt) && expiresAt > Math.floor(Date.now() / 1000) + 30

      if (accessToken && (hasFreshExpiry || !expiresAt)) {
        return accessToken
      }
    } catch {
      // Ignore malformed localStorage entries and continue scanning.
    }
  }

  return null
}

const getSupabaseAccessToken = async (): Promise<string | null> => {
  const storedToken = getStoredSupabaseAccessToken()
  if (storedToken) {
    return storedToken
  }

  try {
    const { data } = await Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Session check timeout')), SESSION_FETCH_TIMEOUT_MS),
      ),
    ])

    return (data as { session?: { access_token?: string | null } })?.session?.access_token || getStoredSupabaseAccessToken()
  } catch (error) {
    console.warn('Supabase session lookup timed out; falling back to stored token.', error)
    return getStoredSupabaseAccessToken()
  }
}

api.interceptors.request.use(
  async (config) => {
    const urlPath = (config.url || '').toLowerCase()
    
    // If Authorization header is already provided by the caller, skip token resolution
    const existingAuth =
      (config.headers as any)?.Authorization ||
      (config.headers as any)?.authorization
    if (existingAuth) {
      return config
    }
    
    if (AUTH_FREE_PATHS.some((path) => urlPath.startsWith(path))) {
      return config
    }

    if (LONG_REQUEST_PATHS.some((path) => urlPath.startsWith(path))) {
      config.timeout = LONG_REQUEST_TIMEOUT_MS
    }

    // For admin endpoints, prefer backend JWT token over Supabase token
    // Admin endpoints require backend JWT tokens, not Supabase tokens
    let token: string | null = null
    
    if (typeof window !== 'undefined') {
      // Check if this is an admin endpoint
      const isAdminEndpoint = urlPath.startsWith('/admin')
      
      if (isAdminEndpoint) {
        // For admin endpoints, prioritize backend JWT token
        const apiToken = localStorage.getItem('trdrhub_api_token')
        if (apiToken) {
          token = apiToken
        } else {
          // Fallback to Supabase token if backend token not available
          token = await getSupabaseAccessToken()
        }
      } else {
        // For non-admin endpoints, use the fastest valid Supabase token path first.
        token = await getSupabaseAccessToken()
        
        if (!token) {
          const apiToken = localStorage.getItem('trdrhub_api_token')
          if (apiToken) {
            token = apiToken
          }
        }
      }
    }

    if (token) {
      const headers = (config.headers ?? {}) as Record<string, string>
      headers.Authorization = `Bearer ${token}`
      config.headers = headers as any
    } else if (urlPath.startsWith('/admin')) {
      // Log warning for admin endpoints without token
      console.warn('Admin endpoint called without authentication token:', config.url)
      console.warn('Please log in via /login page to get backend JWT token')
    }

    // Add org param for bank endpoints
    if (urlPath.startsWith('/bank') && typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      const orgParam = url.searchParams.get('org')
      if (orgParam && config.params) {
        config.params = { ...config.params, org: orgParam }
      } else if (orgParam) {
        config.params = { org: orgParam }
      }
    }

    // Add locale header for i18n
    if (typeof window !== 'undefined') {
      const lang = localStorage.getItem('i18nextLng') || 'en'
      const headers = (config.headers ?? {}) as Record<string, string>
      headers['Accept-Language'] = lang
      config.headers = headers as any
    }

    // Add CSRF token for state-changing methods
    if (config.method && requiresCsrfToken(config.method)) {
      let csrfToken = getCsrfToken()
      
      // If no CSRF token found, try to fetch one before making the request
      if (!csrfToken) {
        try {
          const { fetchCsrfToken } = await import('@/lib/csrf')
          csrfToken = await fetchCsrfToken(API_BASE_URL_VALUE)
        } catch (error) {
          console.warn('Failed to fetch CSRF token:', error)
        }
      }
      
      if (csrfToken) {
        const headers = (config.headers ?? {}) as Record<string, string>
        headers['X-CSRF-Token'] = csrfToken
        config.headers = headers as any
      } else {
        console.warn('CSRF token not available for', config.method, config.url)
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Public marketing/lead-magnet routes where a logged-out visitor is expected.
// A 401 from a session-bootstrap call (e.g. /auth/me on a stale Supabase
// session) MUST NOT bounce a visitor off these pages.
const PUBLIC_PAGE_PATHS = [
  '/', '/check', '/pricing', '/about', '/contact', '/careers',
  '/privacy', '/terms', '/security',
  '/tools', '/technology', '/docs', '/api', '/blog', '/guides',
  '/trdr', '/lc-demo', '/status', '/lcopilot',
  '/sanctions', '/hs-code', '/doc-generator', '/lc-builder',
  '/price-verify',
]
const PUBLIC_EXACT_PAGE_PATHS = ['/proofline']

const isPublicMarketingPath = (path: string): boolean => {
  if (!path) return false
  if (PUBLIC_EXACT_PAGE_PATHS.includes(path)) return true
  // exact match for '/' (don't let it pass via prefix-match)
  if (path === '/') return true
  return PUBLIC_PAGE_PATHS.some((p) => p !== '/' && (path === p || path.startsWith(`${p}/`)))
}

// 401s from these endpoints are session-bootstrap probes — clear the bad
// session, never redirect. Treating these like a "you tried to do something
// authed and failed" call would bounce every logged-out visitor with a stale
// localStorage token off the public marketing pages.
const SESSION_BOOTSTRAP_PATHS = ['/auth/me']

const isSessionBootstrapEndpoint = (urlPath: string): boolean => {
  if (!urlPath) return false
  return SESSION_BOOTSTRAP_PATHS.some((p) => urlPath === p || urlPath.endsWith(p))
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const urlPath = (error?.config?.url || '').toLowerCase()
    const isAdminEndpoint = urlPath.startsWith('/admin')

    if (error?.response?.status === 401) {
      // In guest mode, do not redirect on 401; allow pages to continue.
      if (!GUEST_MODE) {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
        const onAdminRoute = currentPath.startsWith('/admin')

        if (isAdminEndpoint || onAdminRoute) {
          // Admin endpoints use backend JWT, so clear that token specifically
          if (typeof window !== 'undefined') {
            localStorage.removeItem('trdrhub_api_token')
            if (!currentPath.startsWith('/admin/login')) {
              window.location.href = '/admin/login'
            }
          }
        } else if (isSessionBootstrapEndpoint(urlPath) || isPublicMarketingPath(currentPath)) {
          // Session-bootstrap probe (e.g. useAuth() loading /auth/me with a
          // stale Supabase token) OR the visitor is on a public marketing
          // page — silently clear the bad session, never bounce them off.
          clearSupabaseSession()
        } else {
          clearSupabaseSession()
          if (typeof window !== 'undefined' && !currentPath.startsWith('/login')) {
            window.location.href = buildLoginRedirectUrl(window.location)
          }
        }
      }
    } else if (error?.response?.status === 403) {
      // Check if it's a CSRF error or auth error
      const errorCode = error?.response?.data?.code || ''
      const errorDetail = error?.response?.data?.detail || ''
      
      if (errorCode.startsWith('csrf_') || errorDetail.includes('CSRF')) {
        // CSRF token error - try to refresh token and retry
        const { fetchCsrfToken } = await import('@/lib/csrf')
        try {
          const newToken = await fetchCsrfToken(API_BASE_URL_VALUE, { forceRefresh: true })
          // Retry the original request
          const config = error.config
          if (config && newToken) {
            config.headers = config.headers || {}
            if (config.method && requiresCsrfToken(config.method)) {
              config.headers['X-CSRF-Token'] = newToken
            }
            return api.request(config)
          }
        } catch (csrfError) {
          console.error('Failed to refresh CSRF token:', csrfError)
        }
      } else if (errorDetail.includes('Not authenticated') || errorDetail.includes('Authentication failed')) {
        // Authentication error - redirect to login if not already there
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          console.error('403 Forbidden: Not authenticated. Please log in.')
          // Don't auto-redirect for admin pages - let the component handle it
          // window.location.href = '/login'
        }
      } else {
        // Other 403 error - log for debugging
        console.error('403 Forbidden:', errorDetail || errorCode)
      }
    }
    return Promise.reject(error)
  }
)

export { api }
export const API_BASE_URL = api.defaults.baseURL || API_BASE_URL_VALUE
export const __internal = {
  resolveApiBaseUrl,
  buildLoginRedirectUrl,
  getStoredSupabaseAccessToken,
  getSupabaseAccessToken,
}
